import React, { useState } from 'react'
import { useModal } from '../useModal'
import { Button, TextInput } from '@demandlocal/components'
import { FormProvider, useForm } from 'react-hook-form'
import { usePostCreateLaunchLabsConnection } from '../../api/dealership/settings/integrations/usePostCreateLaunchLabsConnection'
import toast from 'react-hot-toast'
import { queryClient } from '../../api/queryClient'
import { useDealership } from '../../store/zustand/dealershipStore'

type FieldValues = {
  launchlabs_api_key: string
  launchlabs_api_token: string
}

export default function LaunchLabsConnectInstructions() {
  const { closeModal } = useModal()
  const dealership = useDealership()
  const formContext = useForm<FieldValues>()
  const [leadsUrl, setLeadsUrl] = useState<string | undefined>(undefined)
  const {
    formState: { isSubmitting },
  } = formContext
  const { mutateAsync } = usePostCreateLaunchLabsConnection()
  const { handleSubmit } = formContext

  const onSubmit = async (values: FieldValues) => {
    const toastId = toast.loading('Connecting your LaunchLabs account')
    const response = await mutateAsync({
      data: {
        launchlabs_api_key: values.launchlabs_api_key,
        launchlabs_api_token: values.launchlabs_api_token,
      },
    })
    setLeadsUrl(response.leadsUrl)
    toast.success('Successfully connected your LaunchLabs account', {
      id: toastId,
    })
    await queryClient.refetchQueries(['3rd_party', 'vendors', dealership?.id])
  }

  if (leadsUrl) {
    return (
      <div className={'flex flex-1 flex-col px-12 pt-12 font-Spartan h-full'}>
        <div className={'flex flex-1 flex-col overflow-auto gap-4'}>
          <div className={'font-bold text-blue-primary text-3xl'}>
            Connecting your LaunchLabs account
          </div>
          <div
            className={
              'flex flex-col h-full items-start justify-center max-w-xl mx-auto'
            }>
            <p className="text-sm text-primary-xiketic font-bold text-center text-xl pb-4">
              Your LaunchLabs account is connected. You can now start creating leads and
              track them.
            </p>
            <p className="text-sm text-web-gray font-bold pb-2 text-start">
              Add this URL as a HTTP POST within Leads & Notifications, in LaunchLabs:
            </p>
            <div
              className={
                'flex w-full items-center justify-between flex-row bg-primary-persian-blue/10 p-2 mb-8'
              }>
              <span className={'text-primary-persian-blue text-sm font-bold'}>
                {leadsUrl}
              </span>
              <span
                onClick={(e) => {
                  e.preventDefault()
                  navigator.clipboard.writeText(leadsUrl)
                  toast.success('Copied to clipboard')
                }}
                className={
                  'text-primary-persian-blue text-sm font-bold ml-3 bg-white p-2 rounded-xl cursor-pointer'
                }>
                Copy
              </span>
            </div>
          </div>
        </div>
        <div
          className={
            'h-20 items-center flex w-full justify-between border-t-2 py-4 mt-4'
          }>
          <Button
            disabled={isSubmitting}
            variant="secondary"
            onClick={(e) => {
              e.preventDefault()
              closeModal()
            }}>
            Close
          </Button>
        </div>
      </div>
    )
  }

  return (
    <FormProvider {...formContext}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={'flex flex-1 flex-col px-12 pt-12 font-Spartan h-full overflow-auto'}>
        <div className={'flex flex-1 flex-col overflow-auto gap-4'}>
          <div className={'font-bold text-blue-primary text-3xl'}>
            Connecting your LaunchLabs account
          </div>
          <div className={'flex flex-col w-1/2 gap-4'}>
            <TextInput
              inverted
              rules={{
                required: 'This field is required',
              }}
              label="LaunchLabs API Token"
              placeholder="Enter your LaunchLabs API key"
              name="launchlabs_api_token"
            />
            <TextInput
              inverted
              rules={{
                required: 'This field is required',
              }}
              label="LaunchLabs API Key"
              placeholder="Enter your LaunchLabs API key"
              name="launchlabs_api_key"
            />
            <p className="text-sm text-web-gray">
              These credentials are required to connect with LaunchLabs services. Get
              these from the API section of your LaunchLabs account.
            </p>
            <p className="text-sm text-web-gray font-bold">
              Launch Labs will provide us an Agency level Secret Key at your Request.
              Email your Launch Labs rep and tell them you work with us.
            </p>
          </div>
        </div>
        <div
          className={
            'h-20 items-center flex w-full justify-between border-t-2 py-4 mt-4'
          }>
          <Button
            disabled={isSubmitting}
            variant="secondary"
            onClick={(e) => {
              e.preventDefault()
              closeModal()
            }}>
            Close
          </Button>
          <Button variant="primary" disabled={isSubmitting}>
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
